// ============================================================================
// useTaxPack — the jurisdiction pack for pickers and the Tax Masters host
// ============================================================================
// One request per mount; the pack is small (a tenant's masters are dozens of
// rows). Re-fetch after authoring through `refetch`.

import { useCallback, useEffect, useState } from 'react';
import { taxService } from 'api/services/taxService';
import type { TaxPack } from 'api/types/tax.types';

export interface UseTaxPackResult {
  pack: TaxPack | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const errorMessage = (err: unknown): string => {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || 'Could not load the tax configuration.';
};

export const useTaxPack = (jurisdictionCode?: string): UseTaxPackResult => {
  const [pack, setPack] = useState<TaxPack | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPack(await taxService.getPack(jurisdictionCode));
    } catch (err) {
      setPack(null);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [jurisdictionCode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const loaded = await taxService.getPack(jurisdictionCode);
        if (!cancelled) setPack(loaded);
      } catch (err) {
        if (!cancelled) {
          setPack(null);
          setError(errorMessage(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jurisdictionCode]);

  return { pack, loading, error, refetch };
};

export default useTaxPack;
